import { useRef } from 'react';
import { MapPin, Navigation, Plane, Ship, Train, TrendingUp } from 'lucide-react';
import { motion, useInView } from 'motion/react';

export function Location() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });

  const advantages = [
    {
      icon: Navigation,
      title: 'Acceso Carretero',
      description: 'Conexión directa a rutas principales y corredores comerciales.',
      stat: '5 min',
    },
    {
      icon: Plane,
      title: 'Cercanía Aérea',
      description: 'Ubicación estratégica a 15 minutos del aeropuerto internacional.',
      stat: '15 min',
    },
    {
      icon: Ship,
      title: 'Puerto Cercano',
      description: 'Acceso ágil a puerto marítimo para operaciones de carga.',
      stat: '20 km',
    },
    {
      icon: Train,
      title: 'Conexión Ferroviaria',
      description: 'Soporte para transporte de carga pesada y consolidada.',
      stat: '10 min',
    },
  ];

  return (
    <section id="location" className="py-20 md:py-32 bg-slate-950 text-white border-t border-slate-800" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/6 border border-white/10 text-slate-200 mb-5"
          >
            <MapPin className="h-4 w-4 text-slate-300" />
            <span className="text-sm">Ubicación Estratégica</span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 18 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl text-white mb-5 tracking-tight"
          >
            En el corazón de la operación logística
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.2 }}
            className="text-lg text-slate-300 leading-relaxed"
          >
            Una ubicación preparada para optimizar tiempos, reducir fricción y conectar con los principales flujos
            de transporte.
          </motion.p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7 }}
            className="relative"
          >
            <div className="rounded-[1.5rem] border border-white/10 bg-white/5 p-6 shadow-sm backdrop-blur-sm">
              <div className="aspect-[4/3] rounded-2xl bg-slate-900 border border-white/10 flex items-center justify-center relative overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(148,163,184,0.12),transparent_45%)]" />
                <div className="absolute inset-0 bg-[linear-gradient(180deg,transparent,rgba(15,23,42,0.3))]" />

                <motion.div
                  animate={{ y: [0, -8, 0] }}
                  transition={{ duration: 2.2, repeat: Infinity }}
                  className="relative z-10"
                >
                  <MapPin className="h-16 w-16 text-slate-200" />
                </motion.div>
              </div>
            </div>

            <div className="absolute -bottom-5 -left-5 rounded-2xl bg-white text-slate-950 p-4 shadow-lg border border-slate-200">
              <div className="text-xs uppercase tracking-[0.22em] text-slate-500">Disponibilidad</div>
              <div className="text-2xl font-semibold mt-1">98%</div>
            </div>
          </motion.div>

          <div className="grid sm:grid-cols-2 gap-4">
            {advantages.map((advantage, index) => (
              <motion.article
                key={advantage.title}
                initial={{ opacity: 0, y: 18 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.15 + index * 0.08 }}
                className="rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur-sm hover:bg-white/8 transition-colors"
                aria-label={advantage.title}
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="w-12 h-12 rounded-xl bg-white text-slate-950 flex items-center justify-center mb-4">
                      <advantage.icon className="h-6 w-6" />
                    </div>
                    <h3 className="text-lg text-white mb-2 font-semibold">{advantage.title}</h3>
                    <p className="text-sm text-slate-300 leading-relaxed">{advantage.description}</p>
                  </div>
                  <div className="px-3 py-1 rounded-full bg-white/8 border border-white/10 text-sm font-semibold text-white whitespace-nowrap">
                    {advantage.stat}
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.4 }}
          className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4"
        >
          {[
            { value: '15 min', label: 'al Aeropuerto' },
            { value: '20 km', label: 'al Puerto' },
            { value: '5 min', label: 'a Autopista' },
            { value: '10 min', label: 'a Terminal Ferroviaria' },
          ].map((stat) => (
            <div key={stat.label} className="rounded-2xl border border-white/10 bg-white/5 p-5 text-center backdrop-blur-sm">
              <div className="text-2xl md:text-3xl text-white font-semibold">{stat.value}</div>
              <div className="text-sm text-slate-300 mt-2">{stat.label}</div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
